import { useState } from 'react';
import { useLazyGetAllProvidersV2Query } from '~/store/services/providers';
import { useDatadog } from '~/lib/hooks/useDatadog';

export type ProvidersSearchData = {
  handleSearch: (searchTerm: string, pageNumber: number, role?: string) => void;
  isLoading: boolean;
  dataList: GetProviderByIdResponse[];
  searchInput: string;
  setSearchInput: (val: string) => void;
  totalCount: number;
  error: string | null;
};

export type UseProvidersSearchDataProps = {
  pageSize?: number;
  addCalendarProfile?: boolean;
};

export const useProvidersSearchData = ({
  pageSize = 15,
  addCalendarProfile = false,
}: UseProvidersSearchDataProps): ProvidersSearchData => {
  const { logError } = useDatadog();
  const [searchInput, setSearchInput] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [dataList, setDataList] = useState<GetProviderByIdResponse[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [getAllProviders, { isFetching }] = useLazyGetAllProvidersV2Query();

  const handleSearch = (
    searchTerm: string,
    pageNumber: number,
    role?: string,
  ): void => {
    if (searchTerm?.trim()) {
      setSearchInput(searchTerm);
      setError(null);

      getAllProviders({
        'include-calendar-profile': addCalendarProfile,
        'page-number': pageNumber,
        'page-size': pageSize,
        'provider-search': searchTerm.trim().split(' ').join('+'),
        role: role ? [role] : undefined,
        status: 'active',
        'sort-by': 'name',
      })
        .unwrap()
        .then((result) => {
          const { data, count } = result ?? {};
          if (data) {
            setDataList(
              (curr: GetProviderByIdResponse[]): GetProviderByIdResponse[] =>
                pageNumber === 1 ? data : [...curr, ...data],
            );
            setTotalCount(count);
            setError(null);
          }
        })
        .catch((err) => {
          logError(err, {
            searchTerm,
            pageNumber,
            role,
          });
          setError('Failed to load providers. Please try again.');
          setDataList([]);
          setTotalCount(0);
        });
    }
  };

  return {
    dataList,
    error,
    handleSearch,
    isLoading: isFetching,
    searchInput,
    setSearchInput: (val: string) => setSearchInput(val),
    totalCount,
  };
};
